import React, { useState } from 'react';
import { Alert, Button, Modal, Space, Typography } from 'antd';
import { UserSwitchOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';

/* Shown when a pending invite was found for the signed-in address but the user already
   belongs to a different organization.

   This state has to be surfaced in the shell rather than on OrgSetupPage: the user does have
   an orgId, so they go straight into the app and never see the setup page. Accepting is
   deliberately behind a confirmation because it rewrites orgId on their profile, and access is
   scoped by that field — everything they built in the old org stays in Firestore but stops
   being reachable for them. */
const InviteConflictBanner: React.FC = () => {
  const { inviteOutcome, acceptInviteConflict, dismissInviteOutcome } = useAuth();
  const { organization, refreshProfile } = useOrg();
  const [confirming, setConfirming] = useState(false);
  const [working, setWorking] = useState(false);

  if (inviteOutcome?.status !== 'conflict') return null;

  const { invite, blockedReason } = inviteOutcome;
  const currentName = organization?.name ?? 'your current organization';
  const roleLabel = invite.role === 'admin' ? 'Admin' : invite.role === 'contractor' ? 'Contractor' : 'Member';

  /* The server says up front when the switch would be refused (you own or are the last admin of
     your current org), so explain that instead of offering a switch that can't go through. */
  if (blockedReason) {
    return (
      <Alert
        banner
        type="warning"
        icon={<UserSwitchOutlined />}
        message={
          <>
            <strong>You have a pending invitation</strong> for {invite.email} as {roleLabel}, but
            you can't leave {currentName} yet. {blockedReason}
          </>
        }
        action={<Button size="small" onClick={dismissInviteOutcome}>Dismiss</Button>}
      />
    );
  }

  const handleAccept = async () => {
    setWorking(true);
    const outcome = await acceptInviteConflict();
    if (outcome.status === 'applied') await refreshProfile();
    setWorking(false);
    setConfirming(false);
  };

  return (
    <>
      <Alert
        banner
        type="info"
        icon={<UserSwitchOutlined />}
        message={
          <>
            <strong>You have a pending invitation</strong> for {invite.email} as {roleLabel}.
            Accepting moves your account out of {currentName}.
          </>
        }
        action={
          <Space>
            <Button size="small" type="primary" onClick={() => setConfirming(true)}>Review</Button>
            <Button size="small" onClick={dismissInviteOutcome}>Not now</Button>
          </Space>
        }
      />
      <Modal
        title="Switch organizations?"
        open={confirming}
        onOk={handleAccept}
        okText={working ? 'Switching…' : 'Switch organizations'}
        okButtonProps={{ danger: true, loading: working }}
        onCancel={() => setConfirming(false)}
        cancelText="Keep my current organization"
      >
        <Typography.Paragraph>
          Your account will move from <strong>{currentName}</strong> into the organization that
          invited <strong>{invite.email}</strong>, as {roleLabel}.
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Projects, assessments and files created under {currentName} are not deleted, but access
          is scoped to the organization on your account — so they will no longer be visible to
          you, and only a remaining admin of {currentName} could restore your access. If anything
          there matters, export or hand it over first.
        </Typography.Paragraph>
      </Modal>
    </>
  );
};

export default InviteConflictBanner;
